import { useQuery } from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'

import { AGENT_URL } from '@/config/env'
import { providerOptionKeys } from '@/lib/queryKeys'

export interface ProviderOption {
  id: string
  name: string
}

// A single page well above any observed org's provider count (dozens, not
// hundreds) — simpler than teaching the filter picker to paginate, at the
// cost of silently truncating an org with more providers than this.
const OPTIONS_LIMIT = 200

async function fetchProviderOptions(token: string): Promise<ProviderOption[]> {
  const response = await fetch(
    `${AGENT_URL}/data/providers?limit=${OPTIONS_LIMIT}&sort_by=provider_name&sort_order=asc`,
    { headers: { Authorization: token } }
  )

  if (!response.ok) {
    throw new Error(`Failed to fetch provider options: ${response.status}`)
  }

  const payload = (await response.json()) as {
    data?: unknown
  }
  const rows: unknown[] = Array.isArray(payload?.data) ? payload.data : []
  return rows
    .map((row: any) => ({
      id: String(row?.id ?? ''),
      name: typeof row?.provider_name === 'string' ? row.provider_name : '',
    }))
    .filter((option): option is ProviderOption => Boolean(option.id))
}

/**
 * The org's providers, for the Appointments filter rail's Provider picker
 * (a compact multi-select `checkbox` field, filtering by id — see
 * appointmentsTableSchema.ts). A single-shot fetch, not the paginated
 * useProvidersQuery the Providers table page uses: a picker needs the whole
 * list up front, not a page at a time.
 */
export function useProviderOptions(enabled = true) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useQuery({
    queryKey: providerOptionKeys.all(organization?.id),
    enabled: enabled && !!organization?.id,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error('No authentication token available')
      return fetchProviderOptions(token)
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}
